/**
 * Display Devices — the "(N)" on the sidebar's Inbox item, e.g. "Inbox (7)".
 *
 * SCOPE: this is deliberately Display-Devices-only, and it all lives inside this
 * module. The sidebar tree is server-owned (GET /menu -> app_mnu_itm_lst_t) and
 * painted by the shared shells, so *something* outside has to fold the number in
 * — that is one line in components/Sidebar.tsx, which is the single sidebar both
 * shells mount (core-shell's Layout and MaudErpLayoutWrapper). Hooking it there
 * rather than in each shell is what makes the badge show up on every login,
 * whatever an officer's landing page happens to be. No other module's menu item
 * is read or rewritten; delete this file and that line and every sidebar label
 * goes back to plain text.
 *
 * The count reaches the sidebar two ways:
 *   • the hook fetches it when the menu loads, so the number is already right
 *     before the officer has ever opened the Inbox;
 *   • InboxPage publishes the count it is already rendering (setInboxBadgeCount),
 *     so forwarding/approving a file drops the badge instantly — no round-trip,
 *     no stale number.
 *
 * WHY THE FETCH IS NOT A ONE-SHOT. It used to be exactly one request at mount,
 * and that left two ways for the badge to simply not be there:
 *   • the request lost the race with session hydration (or failed for any other
 *     reason) and nothing ever asked again, so the officer saw a bare "Inbox"
 *     for the rest of the session unless they opened the page or switched tabs;
 *   • a published 0 stuck. Acting on the last file correctly clears the badge —
 *     but when that file comes back (a send-back is a round trip) or a new one
 *     lands, only a tab switch would have noticed.
 * So a failed pull now retries, and the count is re-read as the officer moves
 * around the app — throttled, because it runs the real inbox query server-side.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import displayDevicesWorkflowService from '../services/displayDevicesWorkflowService';

/** Menu URL of the Inbox item — routes/config.tsx 'inbox' under /app/maud-erp/display-devices. */
export const DD_INBOX_MENU_PATH = '/app/maud-erp/display-devices/inbox';

/**
 * What we actually match on. Menu rows are built in MenuMdl as '/app' || url_tx,
 * but that prefix is conditional (mobile logins, cmpnt_id = 2, get the bare
 * url_tx), so keying on the full path would silently miss. These two segments
 * are still unique to this module's Inbox.
 */
const DD_INBOX_URL_SUFFIX = 'display-devices/inbox';

const BADGE_EVENT = 'maud:dd-inbox-badge';

/** Published by InboxPage — the exact count it is showing in its own header. */
export function setInboxBadgeCount(count: number): void {
  window.dispatchEvent(
    new CustomEvent<number>(BADGE_EVENT, { detail: Number.isFinite(count) ? count : 0 }),
  );
}

/**
 * Match on the trailing segments, so every form of the row matches:
 * '/app/maud-erp/display-devices/inbox', '/maud-erp/display-devices/inbox', or a
 * remount elsewhere. Still specific to this module — a bare '/inbox' item
 * belonging to Water, Sewerage or Marriage can never match.
 */
function isInboxMenuUrl(menuUrl: unknown): boolean {
  if (typeof menuUrl !== 'string' || !menuUrl) return false;
  const a = menuUrl.trim().toLowerCase().replace(/[?#].*$/, '').replace(/\/+$/, '');
  return !!a && a.endsWith(DD_INBOX_URL_SUFFIX);
}

function menuHasInbox(items: any[]): boolean {
  for (const item of items || []) {
    if (isInboxMenuUrl(item?.mnu_itm_url_tx)) return true;
    if (Array.isArray(item?.children) && item.children.length && menuHasInbox(item.children)) return true;
  }
  return false;
}

/**
 * The count is folded into the label rather than added as a separate field
 * because the shell renders that label from ~6 places (list nav, tree view,
 * collapsed popup, secondary panel, search results, drill-down header) — one
 * decorated label keeps every one of them consistent without touching any of them.
 */
function decorate(items: any[], count: number): any[] {
  return (items || []).map((item) => {
    const children = Array.isArray(item?.children) && item.children.length
      ? decorate(item.children, count)
      : item?.children;
    if (!isInboxMenuUrl(item?.mnu_itm_url_tx)) {
      return children === item?.children ? item : { ...item, children };
    }
    return {
      ...item,
      children,
      mnu_itm_nm: `${item.mnu_itm_nm} (${count})`,
      t_mnu_itm_nm: item.t_mnu_itm_nm ? `${item.t_mnu_itm_nm} (${count})` : item.t_mnu_itm_nm,
    };
  });
}

/**
 * Returns the same menu tree with "(N)" appended to the Display Devices Inbox
 * label. A user whose menu has no Display Devices Inbox gets the tree back
 * untouched and fires no request at all.
 */
/** Backoff for a failed count, in ms. A boot-time failure is usually a session
 *  that is a moment behind, so the first retry is quick and they spread out. */
const RETRY_DELAYS_MS = [1500, 4000, 10000];

/** Don't re-read the count more than once in this window. It runs the real
 *  inbox query server-side, and an officer clicking around the rail would
 *  otherwise fire one per navigation. */
const REFRESH_THROTTLE_MS = 30000;

export function useDisplayDevicesInboxBadge<T>(menuItems: T[]): T[] {
  const [count, setCount] = useState(0);

  const hasInbox = useMemo(() => menuHasInbox(menuItems as any[]), [menuItems]);
  const hasInboxRef = useRef(hasInbox);
  hasInboxRef.current = hasInbox;

  // Every timer this hook owns, cleared on unmount so a retry can never land on
  // a gone component (or on the next user, after a logout).
  const timersRef = useRef<number[]>([]);
  const lastPullRef = useRef(0);
  const attemptRef = useRef(0);

  const pull = useCallback(async (opts: { force?: boolean } = {}) => {
    if (!hasInboxRef.current) return;
    const now = Date.now();
    if (!opts.force && now - lastPullRef.current < REFRESH_THROTTLE_MS) return;
    lastPullRef.current = now;
    try {
      setCount(await displayDevicesWorkflowService.getWorkflowInboxCount());
      attemptRef.current = 0;
    } catch {
      // No badge beats a wrong one — the label simply stays undecorated. But a
      // failure at boot is usually a session that has not settled yet, so ask
      // again a few times before giving up for good (a user without read access
      // fails every time and quietly ends up with no badge, as before).
      const delay = RETRY_DELAYS_MS[attemptRef.current];
      if (delay === undefined) return;
      attemptRef.current += 1;
      const id = window.setTimeout(() => { pull({ force: true }); }, delay);
      timersRef.current.push(id);
    }
  }, []);

  useEffect(() => () => {
    timersRef.current.forEach(id => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  useEffect(() => { pull({ force: true }); }, [hasInbox, pull]);

  useEffect(() => {
    const onBadge = (e: Event) => {
      setCount(Number((e as CustomEvent<number>).detail) || 0);
      // A published count is as good as a fetched one — hold the throttle open
      // so navigating away right after acting does not immediately re-ask.
      lastPullRef.current = Date.now();
    };
    window.addEventListener(BADGE_EVENT, onBadge as EventListener);
    return () => window.removeEventListener(BADGE_EVENT, onBadge as EventListener);
  }, []);

  // Moving around the app re-reads the count (throttled). This is what keeps the
  // number honest for an officer working somewhere else in the ERP while files
  // land on their desk — the sidebar is on screen the whole time.
  const { pathname } = useLocation();
  useEffect(() => { pull(); }, [pathname, pull]);

  // Coming back to the tab or the window picks up anything that moved while it
  // was away — another officer forwarding a file here, or the SLA escalation job.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') pull(); };
    const onFocus = () => pull();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [pull]);

  return useMemo(
    // A zero count shows nothing at all — "Inbox (0)" reads like a broken badge.
    () => (hasInbox && count > 0 ? (decorate(menuItems as any[], count) as T[]) : menuItems),
    [menuItems, hasInbox, count],
  );
}

export default useDisplayDevicesInboxBadge;
